/**
 * Media preview inside the Telegram message bubble.
 *
 * Uses a mosaic layout algorithm that considers actual image aspect
 * ratios to pack media items optimally within a 512px-wide rectangle,
 * matching Telegram's native album rendering.
 *
 * Video items are click-to-play: clicking the play button swaps the
 * static thumbnail for an inline <video> element.
 *
 * Audio items render as player widgets with play/pause and a progress bar.
 */

import { useMemo, useState, useRef, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Play, Pause, ImageOff, Music } from 'lucide-react';
import type { MediaPreviewItem } from '../../stores/preview-store';
import { usePreviewSettings } from '../../stores/preview-settings';
import { computeMosaicLayout, type MosaicRect } from './mosaic-layout';

interface MediaPreviewProps {
  items: MediaPreviewItem[];
}

// ---- Video Cell ----

function MediaCell({
  item,
  rect,
  isSingle,
}: {
  item: MediaPreviewItem;
  rect: MosaicRect;
  isSingle: boolean;
}) {
  const { t } = useTranslation('preview');
  const [playing, setPlaying] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const { videoAutoplay } = usePreviewSettings();

  const src = isSingle && item.previewUrl ? item.previewUrl : item.thumbnailUrl;
  const videoSrc = item.objectUrl;

  const style: React.CSSProperties = {
    position: 'absolute',
    left: `${String(rect.x * 100)}%`,
    top: `${String(rect.y * 100)}%`,
    width: `${String(rect.w * 100)}%`,
    height: `${String(rect.h * 100)}%`,
  };

  const handlePlay = useCallback(() => {
    if (!videoSrc) return;
    setPlaying(true);
  }, [videoSrc]);

  const handleVideoEnd = useCallback(() => {
    if (!videoAutoplay) {
      setPlaying(false);
    }
  }, [videoAutoplay]);

  if (item.isMissing && !src && !videoSrc) {
    return (
      <div className="tg-media-cell tg-media-missing" style={style}>
        <ImageOff className="h-6 w-6 text-muted-foreground/40" />
      </div>
    );
  }

  // Video: show first frame with play overlay when autoplay is off, otherwise inline video
  if (item.type === 'video' && videoSrc) {
    // Autoplay off & not playing: render <video preload="auto"> to show first frame (like the card)
    if (!videoAutoplay && !playing) {
      return (
        <div className="tg-media-cell" style={style}>
          <video
            src={videoSrc}
            preload="auto"
            muted
            playsInline
            className="h-full w-full object-cover"
          />
          <button
            type="button"
            className="absolute inset-0 flex cursor-pointer items-center justify-center border-0 bg-transparent p-0"
            onClick={handlePlay}
            aria-label={t('playVideo')}
          >
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-black/50">
              <Play className="h-5 w-5 fill-white text-white" />
            </div>
          </button>
        </div>
      );
    }

    return (
      <div className="tg-media-cell" style={style}>
        <video
          ref={videoRef}
          src={videoSrc}
          poster={src ?? undefined}
          preload="auto"
          controls={playing}
          autoPlay
          muted={videoAutoplay && !playing}
          loop={videoAutoplay && !playing}
          onEnded={handleVideoEnd}
          onError={handleVideoEnd}
          className="h-full w-full object-cover"
        />
      </div>
    );
  }

  return (
    <div className="tg-media-cell" style={style}>
      {src ? (
        <img
          src={src}
          alt=""
          className="h-full w-full object-cover"
          draggable={false}
        />
      ) : (
        <div className="h-full w-full bg-muted" />
      )}
    </div>
  );
}

// ---- Audio Player Widget ----

function formatTime(seconds: number): string {
  const mins = String(Math.floor(seconds / 60));
  const secs = String(Math.floor(seconds % 60)).padStart(2, '0');
  return `${mins}:${secs}`;
}

function AudioPlayerWidget({ item }: { item: MediaPreviewItem }) {
  const { t } = useTranslation('preview');
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const duration = item.durationSeconds ?? 0;
  const audioSrc = item.objectUrl;

  const togglePlay = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
    } else {
      void audio.play().catch(() => {
        // Playback failed (e.g. blob URL expired)
      });
    }
  }, [isPlaying, audioSrc]);

  const handleTimeUpdate = useCallback(() => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  }, []);

  const handlePlay = useCallback(() => { setIsPlaying(true); }, []);
  const handlePause = useCallback(() => { setIsPlaying(false); }, []);
  const handleEnded = useCallback(() => {
    setIsPlaying(false);
    setCurrentTime(0);
  }, []);

  // Pause when objectUrl changes (draft switch)
  useEffect(() => {
    return () => {
      audioRef.current?.pause();
    };
  }, [audioSrc]);

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div className="flex items-center gap-3 rounded-lg bg-[var(--tg-bubble-bg,hsl(var(--muted)))] p-2">
      {/* Play/Pause button */}
      {audioSrc ? (
        <button
          type="button"
          onClick={togglePlay}
          className="flex h-12 w-12 flex-shrink-0 cursor-pointer items-center justify-center overflow-hidden rounded-md border-0 bg-muted p-0"
          aria-label={isPlaying ? t('pause') : t('play')}
        >
          {item.thumbnailUrl ? (
            <div className="relative h-full w-full">
              <img
                src={item.thumbnailUrl}
                alt=""
                className="h-full w-full object-cover"
                draggable={false}
              />
              <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                {isPlaying ? (
                  <Pause className="h-5 w-5 fill-white text-white" />
                ) : (
                  <Play className="h-4 w-4 fill-white text-white" />
                )}
              </div>
            </div>
          ) : isPlaying ? (
            <Pause className="h-5 w-5 text-muted-foreground" />
          ) : (
            <Play className="h-5 w-5 text-muted-foreground" />
          )}
          <audio
            ref={audioRef}
            src={audioSrc}
            preload="metadata"
            onTimeUpdate={handleTimeUpdate}
            onPlay={handlePlay}
            onPause={handlePause}
            onEnded={handleEnded}
            onError={() => {
              // Audio source unavailable (e.g. blob URL revoked)
            }}
          />
        </button>
      ) : (
        <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center overflow-hidden rounded-md bg-muted">
          {item.thumbnailUrl ? (
            <img
              src={item.thumbnailUrl}
              alt=""
              className="h-full w-full object-cover"
              draggable={false}
            />
          ) : (
            <Music className="h-5 w-5 text-muted-foreground/50" />
          )}
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="truncate text-sm font-medium text-foreground">
          {item.title ?? t('unknownTitle')}
        </span>
        <span className="truncate text-xs text-muted-foreground">
          {item.performer ?? t('unknownArtist')}
        </span>
        {/* Progress bar + time */}
        {duration > 0 && (
          <div className="flex items-center gap-2">
            <div className="h-1 flex-1 overflow-hidden rounded-full bg-muted-foreground/20">
              <div
                className="h-full rounded-full bg-primary transition-[width] duration-200"
                style={{ width: `${String(progress)}%` }}
              />
            </div>
            <span className="flex-shrink-0 text-[10px] tabular-nums text-muted-foreground">
              {isPlaying || currentTime > 0 ? formatTime(currentTime) : formatTime(duration)}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

// ---- Main Component ----

export function MediaPreview({ items }: MediaPreviewProps) {
  const isAudioMode = items.length > 0 && items[0]?.type === 'audio';

  const layout = useMemo(() => {
    if (items.length === 0 || isAudioMode) return null;
    return computeMosaicLayout(
      items.map((it) => ({ width: it.width, height: it.height })),
    );
  }, [items, isAudioMode]);

  // Audio mode: render audio player widgets instead of mosaic
  if (isAudioMode) {
    return (
      <div className="flex flex-col gap-1">
        {items.map((item) => (
          <AudioPlayerWidget key={item.id} item={item} />
        ))}
      </div>
    );
  }

  if (!layout || layout.rects.length === 0) return null;

  const isSingle = items.length === 1;

  return (
    <div
      className="tg-media-grid"
      style={{
        position: 'relative',
        width: '100%',
        maxWidth: layout.width,
        aspectRatio: `${String(layout.width)} / ${String(layout.height)}`,
      }}
    >
      {layout.rects.map((rect) => {
        const item = items[rect.index];
        if (!item) return null;
        return (
          <MediaCell
            key={item.id}
            item={item}
            rect={rect}
            isSingle={isSingle}
          />
        );
      })}
    </div>
  );
}
